// /frontend/src/pages/DesignerPackages.jsx - Simplified Clean Version
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout';
import { cartService } from '../services/cartService';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Product Selection Modal
const PackageSelectionModal = ({ packageId, show, onHide }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [currentCategory, setCurrentCategory] = useState('');
  const [currentItemKey, setCurrentItemKey] = useState('');
  const [availableProducts, setAvailableProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  const fetchProductsByCategory = async (category) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/products?category=${category}&limit=10`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      return data.success && data.products ? data.products : [];
    } catch (error) {
      console.error(`Error fetching ${category}:`, error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && packageId) {
      const loadProducts = async () => {
        setLoading(true);
        const categories = ['table', 'chair', 'stool', 'sofa', 'bed', 'cabinet'];
        const productData = {};
        
        for (const category of categories) {
          const products = await fetchProductsByCategory(category);
          if (products.length > 0) {
            productData[category] = products.map(product => ({
              id: product._id,
              name: product.name,
              price: product.isOnSale ? product.salePrice : product.price,
              image: product.images && product.images[0] ? product.images[0] : '/placeholder.jpg'
            }));
          }
        }
        setAvailableProducts(productData);
        setLoading(false);
      };
      
      setAvailableProducts({});
      setSelectedProducts({});
      loadProducts();
    }
  }, [show, packageId]);

  const getProductKey = (itemName) => {
    const name = itemName.toLowerCase();
    if (name.includes('table')) return 'table';
    if (name.includes('chair')) return 'chair';
    if (name.includes('stool')) return 'stool';
    if (name.includes('sofa')) return 'sofa';
    if (name.includes('bed')) return 'bed';
    return 'cabinet';
  };

  const getPackageData = (id) => {
    const packages = {
      'one-bedroom': {
        title: '1 Bedroom Package',
        rooms: {
          'Kitchen': ['Counter Stool', 'Counter Stool'],
          'Dining Area': ['Dining Table', 'Dining Chair', 'Dining Chair'],
          'Living Area': ['Modular Sofa', 'Coffee Table', 'Side Table', 'Table Lamp', 'Lounge Chair'],
          'Primary Bedroom': ['Eastern King Bed Frame', 'Nightstand', 'Nightstand', 'Table Lamp', 'Dresser']
        }
      },
      'two-bedroom': {
        title: '2 Bedroom Package',
        rooms: {
          'Kitchen': ['Counter Stool', 'Counter Stool', 'Counter Stool'],
          'Dining Area': ['Dining Table', 'Dining Chair', 'Dining Chair', 'Dining Chair', 'Dining Chair'],
          'Living Area': ['Modular Sofa', 'Coffee Table', 'Side Table', 'Table Lamp', 'Lounge Chair'],
          'Primary Bedroom': ['Eastern King Bed Frame', 'Nightstand', 'Nightstand', 'Table Lamp', 'Dresser'],
          'Bedroom 2': ['Queen Bed Frame', 'Nightstand', 'Table Lamp']
        }
      },
      'three-bedroom': {
        title: '3 Bedroom Package',
        rooms: {
          'Kitchen': ['Counter Stool', 'Counter Stool', 'Counter Stool'],
          'Dining Area': ['Dining Table', 'Dining Chair', 'Dining Chair', 'Dining Chair'],
          'Living Area': ['Modular Sofa', 'Coffee Table', 'Side Table', 'Table Lamp', 'Lounge Chair'],
          'Primary Bedroom': ['Eastern King Bed Frame', 'Nightstand', 'Nightstand', 'Table Lamp', 'Dresser'],
          'Bedroom 2': ['Queen Bed Frame', 'Nightstand', 'Table Lamp'],
          'Bedroom 3': ['Queen Bed Frame', 'Nightstand', 'Table Lamp']
        }
      }
    };
    return packages[id] || null;
  };

  const handleProductSelect = (itemKey, product) => {
    setSelectedProducts(prev => ({ ...prev, [itemKey]: product }));
    setShowProductSelector(false);
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', '/designer-packages');
      window.location.href = '/login';
      return;
    }
    
    const selectedCount = Object.keys(selectedProducts).length;
    
    if (selectedCount < 1) {
      toast.error('Please select at least 1 product to add this package to cart.');
      return;
    }
    
    try {
      setAddingToCart(true);
      
      const addPromises = Object.values(selectedProducts).map(async (product) => {
        return await cartService.addToCart(product.id, 1, {});
      });
      
      await Promise.all(addPromises);
      
      toast.success(`Package Items Added to Cart! ${selectedCount} items added successfully`);
      
      onHide();
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add items to cart. Please try again.');
    } finally {
      setAddingToCart(false);
    }
  };

  if (!show || !packageId) return null;
  const packageData = getPackageData(packageId);
  if (!packageData) return null;

  const selectedCount = Object.keys(selectedProducts).length;
  const totalItems = Object.values(packageData.rooms).reduce((total, items) => total + items.length, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Configure {packageData.title}</h2>
            <p className="text-teal-600">Select products for all items</p>
          </div>
          <button onClick={onHide} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Select Your Products</h3>
            <div className="text-sm text-gray-600">{selectedCount} / {totalItems} items selected</div>
          </div>
          
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <p className="text-red-800 text-sm">
              <strong>Required:</strong> You must select a specific product for every item in this package.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(packageData.rooms).map(([roomName, items]) => (
              <div key={roomName} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-bold text-gray-800 mb-3">{roomName}:</h4>
                <div className="space-y-3">
                  {items.map((item, index) => {
                    const productKey = getProductKey(item);
                    const itemKey = `${roomName}-${item}-${index}`;
                    const selectedProduct = selectedProducts[itemKey];
                    
                    return (
                      <div key={index} className={`p-3 rounded-lg border-2 ${selectedProduct ? 'border-green-300 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{item}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${selectedProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {selectedProduct ? '✓ Selected' : '⚠ Required'}
                          </span>
                        </div>
                        
                        {selectedProduct ? (
                          <div className="space-y-2">
                            <div className="text-xs text-green-700">
                              <strong>{selectedProduct.name}</strong>
                            </div>
                            <button
                              onClick={() => {
                                setCurrentCategory(productKey);
                                setCurrentItemKey(itemKey);
                                setShowProductSelector(true);
                              }}
                              className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded hover:bg-blue-200 w-full"
                            >
                              Change Selection
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setCurrentCategory(productKey);
                              setCurrentItemKey(itemKey);
                              setShowProductSelector(true);
                            }}
                            className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200 w-full font-medium"
                          >
                            ⚠ Select Product (Required)
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <div className="flex-1">
              <div className="text-lg font-semibold text-teal-800 mb-2">{packageData.title}</div>
              <p className="text-sm text-gray-600">Package configured with selected products</p>
            </div>
            <div className="flex gap-3">
              {!isAuthenticated ? (
                <button
                  onClick={handleAddToCart}
                  className="px-8 py-3 rounded-lg font-medium transition bg-gray-700 text-white hover:bg-gray-800"
                >
                  Log in to Purchase
                </button>
              ) : selectedCount >= 1 ? (
                <button
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  className={`px-8 py-3 rounded-lg font-medium transition ${addingToCart ? 'bg-teal-600 text-white cursor-wait' : 'bg-teal-700 text-white hover:bg-teal-800'}`}
                >
                  {addingToCart ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Adding to Cart...
                    </>
                  ) : (
                    'Add to Cart'
                  )}
                </button>
              ) : (
                <button
                  disabled
                  className="px-8 py-3 rounded-lg font-medium transition bg-gray-300 text-gray-500 cursor-not-allowed"
                >
                  Select at least 1 product ({selectedCount} selected)
                </button>
              )}
              <button onClick={onHide} className="border border-gray-300 px-8 py-3 rounded-lg hover:bg-gray-50 transition font-medium">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-60">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Select {currentCategory.toUpperCase()}</h3>
                <button onClick={() => setShowProductSelector(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
              </div>
              
              {loading && (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600 mb-4"></div>
                  <p className="text-gray-600">Loading {currentCategory} products...</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {availableProducts[currentCategory] && availableProducts[currentCategory].length > 0 ? (
                  availableProducts[currentCategory].map((product) => (
                    <div key={product.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="w-full h-40 object-cover rounded-lg mb-3 bg-gray-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                        }}
                      />
                      <h4 className="font-medium mb-2">{product.name}</h4>
                      <button
                        onClick={() => handleProductSelect(currentItemKey, product)}
                        className="w-full bg-teal-700 text-white py-2 rounded hover:bg-teal-800 transition"
                      >
                        Select This Product
                      </button>
                    </div>
                  ))
                ) : !loading && (
                  <div className="col-span-3 text-center py-8">
                    <p className="text-gray-600 mb-2">No {currentCategory} products available</p>
                    <button 
                      onClick={async () => {
                        const products = await fetchProductsByCategory(currentCategory);
                        if (products.length > 0) {
                          setAvailableProducts(prev => ({
                            ...prev,
                            [currentCategory]: products.map(product => ({
                              id: product._id,
                              name: product.name,
                              price: product.isOnSale ? product.salePrice : product.price,
                              image: product.images && product.images[0] ? product.images[0] : '/placeholder.jpg'
                            }))
                          }));
                        }
                      }}
                      className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
                    >
                      Retry Loading
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Package Info Modal  
const PackageInfoModal = ({ packageId, show, onHide }) => {
  const getPackageData = (id) => {
    const packages = {
      'one-bedroom': {
        title: '1 Bedroom Package',
        price: '$35,888',
        itemCount: 46,
        description: 'Complete 1 bedroom home package designed by our professional interior design team.',
        images: ['/api/placeholder/600/400'],
        features: [
          'Professional interior design consultation included',
          'Coordinated color palette and style',
          'Delivery and installation included',
          '30-day satisfaction guarantee'
        ]
      },
      'two-bedroom': {
        title: '2 Bedroom Package',
        price: '$47,888',
        itemCount: 60,
        description: 'Complete 2 bedroom home package perfect for growing families.',
        images: ['/api/placeholder/600/400'],
        features: [
          'Professional interior design consultation included',
          'Coordinated color palette across all rooms',
          'Delivery and installation included',
          '30-day satisfaction guarantee'
        ]
      },
      'three-bedroom': {
        title: '3 Bedroom Package',
        price: '$58,888',
        itemCount: 73,
        description: 'Complete 3 bedroom home package ideal for families.',
        images: ['/api/placeholder/600/400'],
        features: [
          'Professional interior design consultation included',
          'Complete home solution with cohesive design',
          'Delivery and installation included',
          '30-day satisfaction guarantee'
        ]
      }
    };
    return packages[id] || null;
  };

  if (!show || !packageId) return null;
  const packageData = getPackageData(packageId);
  if (!packageData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{packageData.title}</h2>
            <p className="text-teal-600 font-medium">{packageData.itemCount} Items Included</p>
          </div>
          <button onClick={onHide} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        <div className="p-6">
          <div className="mb-8">
            <img src={packageData.images[0]} alt={packageData.title} className="w-full h-80 object-cover rounded-lg bg-gray-200" />
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-3">Package Description</h3>
            <p className="text-gray-600 mb-4">{packageData.description}</p>
            
            <h4 className="text-lg font-semibold mb-2">What's Included:</h4>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              {packageData.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-semibold text-gray-800">Starting Price: {packageData.price}</h4>
                <p className="text-sm text-gray-600">*Final price depends on product selections</p>
              </div>
              <button onClick={onHide} className="bg-teal-700 text-white px-6 py-2 rounded-lg hover:bg-teal-800 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const DesignerPackages = () => {
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [activeTab, setActiveTab] = useState('preDesigned');

  const handleViewPackage = (packageId) => {
    setSelectedPackageId(packageId);
    setShowInfoModal(true);
  };

  const handleAddToCartClick = (packageId) => {
    setSelectedPackageId(packageId);
    setShowSelectionModal(true);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4 text-gray-800">Furniture Packages</h1>
        
        <div className="bg-teal-600 rounded-lg overflow-hidden shadow-sm mb-6">
          <div className="p-4">
            <h2 className="text-xl font-semibold text-white">Pre-Designed 'Set Design Package'</h2>
            <p className="mt-2 text-sm text-white">
              Select from one of our curated packages, which include a fixed assortment of furnishings.
              Each package has been thoughtfully designed by our professional team to create cohesive, 
              designer-quality spaces with no guesswork.
            </p>
          </div>
          
          <div className="bg-yellow-100 border-l-4 border-yellow-300 p-2 mx-4 mb-4 rounded">
            <p className="text-xs text-gray-800">
              <span className="font-bold">Note:</span> Client assumes responsibility for ensuring all included items fit their space.
            </p>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4">
        <div className="border-b border-gray-200 mb-8">
          <div className="flex">
            <button 
              className={`py-2 px-4 text-lg font-medium border-b-2 ${activeTab === 'preDesigned' ? 'border-teal-800 text-teal-800' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('preDesigned')}
            >
              Pre-Designed Packages
            </button>
            <button 
              className={`py-2 px-4 text-lg font-medium border-b-2 ${activeTab === 'fullHome' ? 'border-teal-800 text-teal-800' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('fullHome')}
            >
              Full Home Packages
            </button>
          </div>
        </div>
        
        {activeTab === 'preDesigned' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { id: 'one-bedroom', title: '1 Bedroom Package', price: '$35,888', items: 46, number: '1' },
              { id: 'two-bedroom', title: '2 Bedroom Package', price: '$47,888', items: 60, number: '2' },
              { id: 'three-bedroom', title: '3 Bedroom Package', price: '$58,888', items: 73, number: '3' }
            ].map((pkg) => (
              <div key={pkg.id} className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="h-64 bg-gray-100 relative flex items-center justify-center">
                  <div className="absolute top-2 right-2 bg-teal-700 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
                    Set Package
                  </div>
                  
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <div className="w-32 h-32 bg-teal-50 rounded-full flex items-center justify-center border-4 border-teal-600">
                      <span className="text-7xl font-bold text-teal-700">{pkg.number}</span>
                    </div>
                    <p className="mt-4 font-bold text-teal-700 text-xl">BEDROOM{pkg.number !== '1' ? 'S' : ''}</p>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold mb-2">{pkg.title}</h3>
                  <p className="text-gray-600 mb-4">Complete {pkg.number} bedroom home package with {pkg.items} pre-set furnishing items.</p>
                  <p className="text-xl font-bold text-teal-800 mb-4">Starting at {pkg.price}</p>
                  <div className="flex space-x-3">
                    <button 
                      onClick={() => handleAddToCartClick(pkg.id)}
                      className="bg-teal-700 text-white py-2 px-4 rounded w-full hover:bg-teal-800 transition"
                    >
                      Add to Cart
                    </button>
                    <button 
                      onClick={() => handleViewPackage(pkg.id)}
                      className="border border-gray-300 py-2 px-4 rounded w-full hover:bg-gray-50 transition"
                    >
                      View Package
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Modals */}
        {showSelectionModal && selectedPackageId && (
          <PackageSelectionModal
            packageId={selectedPackageId}
            show={showSelectionModal}
            onHide={() => setShowSelectionModal(false)}
          />
        )}
        
        {showInfoModal && selectedPackageId && (
          <PackageInfoModal
            packageId={selectedPackageId}
            show={showInfoModal}
            onHide={() => setShowInfoModal(false)}
          />
        )}
      </div>
      
      <div className="py-12"></div>
    </Layout>
  );
};

export default DesignerPackages;